import React, { useEffect, useMemo, useState, useCallback } from 'react'
import { Box, Typography, useTheme } from '@mui/material'
import { Trans, msg } from '@lingui/macro'
import { useLingui } from '@lingui/react'
import { CRASH_ID_KEY } from '../utils'
import { useMergeContext } from '../../../store/mergeContext'
import { useLocation } from 'react-router-dom'
import { NewApiBaseUrl } from '../../../consts/new-api-base-url'
import useHttpApiNew from '../../../hooks/useHttpApiNew'
import {
  BASIC_TO_ROLE,
  KIND_TO_BASIC,
  ROLE_TO_BASIC,
  dedupeMergeImages,
  getOrgLabelByBasic,
  isMergeImageReference,
  normalizeInsuranceMatch,
} from '../merge-constants'
import {
  getPersonKey,
  matchesCandidate,
  prepareMatchForMerge,
  setPersonIncludeInMerge,
  collectPeopleFromCrash,
} from '../match-display-rows'
import { buildMatchGroups, getCrashIdFromRecord } from '../match-grid'
import CrashMatchGroupList from './CrashMatchGroupList'

function getImagesFromPoliceMatch(selectedMatch) {
  const images = selectedMatch?.crash?.images
  if (!Array.isArray(images) || images.length === 0) {
    return []
  }

  return images.filter(isMergeImageReference)
}

function mergePolicePhotoLists(...lists) {
  return dedupeMergeImages(lists.flat().filter(Boolean))
}

function prepareInsuranceMatchForMerge(myMatch, current, selectedCrashId) {
  const normalized = normalizeInsuranceMatch(myMatch)
  if (!normalized?.people) return normalized
  return {
    ...normalized,
    people: normalized.people.map((person) => ({
      ...person,
      isMatchCandidate: matchesCandidate(person, myMatch),
      includeInMerge:
        current?.crash?.[CRASH_ID_KEY] === selectedCrashId
          ? current.people?.find((p) => getPersonKey(p) === getPersonKey(person))?.includeInMerge
          : matchesCandidate(person, myMatch),
    })),
  }
}

/**
 * @param {'POLICE' | 'HOSPITAL' | 'INSURANCE'} type
 * @param {number} [targetBasic]
 */
const PossibleMatchGrid = ({ type = 'HOSPITAL', targetBasic }) => {
  const { _ } = useLingui()
  const [matches, setMatches] = useState([])
  const [selectedMatch, setSelectedMatch] = useState(null)
  const { request } = useHttpApiNew()

  const organizationBasic = targetBasic ?? KIND_TO_BASIC[type]
  const role = BASIC_TO_ROLE[organizationBasic]

  const { selectedCrash, setCrashForRole, setPolicePhotos, crashByRole, anchorDetail, anchorRole, collisionChoicesByRole, setCollisionChoice, clearCollisionChoicesForRole } = useMergeContext()
  const storedMatch = crashByRole?.[role]
  const myLocation = useLocation()
  const token = myLocation.state?.token || null
  const init = useMemo(
    () => myLocation.state?.init || {},
    [myLocation.state?.init]
  )
  const theme = useTheme()
  const configuration = init?.configuration
  const organizations = useMemo(
    () => myLocation.state?.init?.organizations || [],
    [myLocation.state?.init?.organizations]
  )

  const categoryFallbacks = useMemo(
    () => ({
      police: _(msg`Police`),
      hospital: _(msg`Hospital`),
      insurance: _(msg`Insurance`),
    }),
    [_],
  )

  const gridTitle = useMemo(() => {
    const orgLabel = getOrgLabelByBasic(organizationBasic, organizations, categoryFallbacks)
    return `Possible ${orgLabel} matches`
  }, [organizationBasic, organizations, categoryFallbacks])

  const anchorRoleLabel = useMemo(
    () => getOrgLabelByBasic(ROLE_TO_BASIC[anchorRole] ?? KIND_TO_BASIC.HOSPITAL, organizations, categoryFallbacks),
    [anchorRole, organizations, categoryFallbacks],
  )

  const matchRoleLabel = useMemo(
    () => getOrgLabelByBasic(organizationBasic, organizations, categoryFallbacks),
    [organizationBasic, organizations, categoryFallbacks],
  )

  const anchorPeople = useMemo(
    () => collectPeopleFromCrash(anchorDetail ?? {}),
    [anchorDetail],
  )

  const getMatches = useCallback(async () => {
    const matchUrlReduced = new URL(`${NewApiBaseUrl}/match/${selectedCrash}`)
    matchUrlReduced.searchParams.set('format', 'table')
    matchUrlReduced.searchParams.set('organization', String(organizationBasic))
    const res = await request(matchUrlReduced, 'GET', null, { Authorization: `Bearer ${token}` })
    if (res.responseAnyError) return
    setMatches(res.responseData?.matches ?? [])
  }, [token, selectedCrash, organizationBasic, request])

  useEffect(() => {
    if (selectedCrash) {
      getMatches()
    }
  }, [selectedCrash, getMatches])

  const handleTogglePersonInclusion = useCallback((personKey, included) => {
    if (!storedMatch || !personKey) return
    setCrashForRole(role, setPersonIncludeInMerge(storedMatch, personKey, included))
  }, [role, setCrashForRole, storedMatch])

  const matchGroups = useMemo(
    () => buildMatchGroups(matches ?? [], configuration ?? {}, {
      organizationBasic,
      getStoredPeople: (match) =>
        storedMatch?.crash?.[CRASH_ID_KEY] === getCrashIdFromRecord(match)
          ? storedMatch?.people
          : undefined,
    }),
    [matches, configuration, organizationBasic, storedMatch],
  )

  const relatedMatchRecords = useMemo(
    () => (matches ?? []).filter((match) => getCrashIdFromRecord(match) === selectedMatch),
    [matches, selectedMatch],
  )

  const matchDetailForPairing = useMemo(() => {
    if (!storedMatch) return null
    if (relatedMatchRecords.length > 1) {
      return { ...storedMatch, _matchMetas: relatedMatchRecords }
    }
    if (relatedMatchRecords.length === 1) {
      const meta = relatedMatchRecords[0]
      return {
        ...storedMatch,
        candidate_name: meta?.candidate_name ?? storedMatch.candidate_name,
        candidate_surname: meta?.candidate_surname ?? storedMatch.candidate_surname,
        _matchMetas: relatedMatchRecords,
      }
    }
    return storedMatch
  }, [storedMatch, relatedMatchRecords])

  const pairingsForSelectedGroup = useMemo(() => {
    if (!selectedMatch || !matchDetailForPairing) return null
    return {
      anchorPeople,
      matchDetail: matchDetailForPairing,
      matchRole: role,
      matchRoleLabel,
      anchorRoleLabel,
      collisionChoices: collisionChoicesByRole[role] ?? {},
      onCollisionChoice: (matchPersonKey, anchorPersonKey) => {
        setCollisionChoice(role, matchPersonKey, anchorPersonKey)
      },
    }
  }, [
    selectedMatch,
    matchDetailForPairing,
    anchorPeople,
    role,
    matchRoleLabel,
    anchorRoleLabel,
    collisionChoicesByRole,
    setCollisionChoice,
  ])

  useEffect(() => {
    if (!selectedMatch) return

    const relatedMatches = (matches ?? []).filter(
      (m) => getCrashIdFromRecord(m) === selectedMatch,
    )
    const myMatch = relatedMatches.length > 1
      ? { ...relatedMatches[0], _matchMetas: relatedMatches }
      : relatedMatches[0]
    if (!myMatch) return

    const current = crashByRole?.[role]
    if (current?.crash?.[CRASH_ID_KEY] === selectedMatch) {
      if (role === 'police') {
        setPolicePhotos((prev) => mergePolicePhotoLists(prev, getImagesFromPoliceMatch(myMatch)))
      }
      return
    }

    const nextDetail =
      role === 'insurance'
        ? prepareInsuranceMatchForMerge(myMatch, current, selectedMatch)
        : prepareMatchForMerge(myMatch)

    clearCollisionChoicesForRole(role)
    setCrashForRole(role, nextDetail)
    if (role === 'police') {
      setPolicePhotos((prev) => mergePolicePhotoLists(prev, getImagesFromPoliceMatch(myMatch)))
    }
  }, [selectedMatch, matches, role, setCrashForRole, setPolicePhotos, crashByRole, clearCollisionChoicesForRole])

  return (
    <Box>
      <Typography
        sx={{
          width: '100%',
          mt: 2,
          mb: 1,
          color: theme.palette.mode === 'light' ? theme.palette.newColors.gray : 'white',
          fontSize: 20,
          fontWeight: 'bold',
        }}
        variant="h5"
      >
        {gridTitle}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        <Trans>
          Click a crash to select the match. Expand to review units and people. Use the Include switch for other people in the same crash.
        </Trans>
      </Typography>
      <CrashMatchGroupList
        groups={matchGroups}
        configuration={configuration}
        organizationBasic={organizationBasic}
        selectedCrashId={selectedMatch}
        onSelectGroup={setSelectedMatch}
        selectable
        showIncludeColumn
        onTogglePersonInclusion={handleTogglePersonInclusion}
        pairingsForSelectedGroup={pairingsForSelectedGroup}
      />
    </Box>
  )
}

export default PossibleMatchGrid
